/**
 * Browser routes aligned with OpenAPI org-scoped paths (minus /orgs/{orgId} prefix).
 *
 * Pattern: /{domain}/{resource}[/:id][/sub-resource]
 */

export const domainRoutes = {
  home: "/home",
  dashboard: "/dashboard",
  search: "/search",
  help: "/help",

  catalog: {
    products: "/catalog/products",
    productDetail: (productId: string) => `/catalog/products/${productId}`,
  },

  content: {
    /** Library — ContentProject list */
    projects: "/content/projects",
    projectDetail: (contentProjectId: string) =>
      `/content/projects/${contentProjectId}`,
    renditionDetail: (contentProjectId: string, renditionId: string) =>
      `/content/projects/${contentProjectId}/renditions/${renditionId}`,
    /** Production — FactoryRun list / workspace */
    production: "/content/production",
    productionNew: "/content/production/new",
    productionRunDetail: (factoryRunId: string) =>
      `/content/production/${factoryRunId}`,
    productionRunProduce: (factoryRunId: string, formatType: string) =>
      `/content/production/${factoryRunId}/produce/${formatType}`,
    productionRunReady: (
      factoryRunId: string,
      formatType?: string,
      params?: { renditionId?: string },
    ) => {
      const base = formatType
        ? `/content/production/${factoryRunId}/ready/${formatType}`
        : `/content/production/${factoryRunId}/ready`;
      if (!params?.renditionId) return base;
      const q = new URLSearchParams({ renditionId: params.renditionId });
      return `${base}?${q.toString()}`;
    },
    assets: "/content/assets",
    assetDetail: (assetId: string) => `/content/assets/${assetId}`,
    templates: "/content/templates",
    taxonomy: "/content/taxonomy",
    versions: "/content/versions",
    tags: "/content/tags",
    hero: "/content/hero",
  },

  publishing: {
    queue: "/publishing/queue",
    calendar: "/publishing/calendar",
    approvals: "/publishing/approvals",
    releases: "/publishing/releases",
    publicationsNew: "/publishing/publications/new",
    publicationDetail: (publicationId: string) =>
      `/publishing/publications/${publicationId}`,
    manifestDetail: (manifestId: string) =>
      `/publishing/manifests/${manifestId}`,
    quick: "/publishing/quick",
    /** Quick Publish with optional seed query params */
    quickWith: (params?: {
      publicationId?: string;
      workId?: string;
      destinationId?: string;
      capability?: string;
      platform?: string;
    }) => {
      const query = new URLSearchParams({ mode: "quick" });
      if (params?.publicationId) query.set("publicationId", params.publicationId);
      if (params?.workId) query.set("workId", params.workId);
      if (params?.destinationId) query.set("destinationId", params.destinationId);
      if (params?.capability) query.set("capability", params.capability);
      if (params?.platform) query.set("platform", params.platform);
      return `/publishing/quick?${query.toString()}`;
    },
    /** Create Publication wizard with optional seed query params */
    publicationsNewWith: (params?: {
      workId?: string;
      contentProjectId?: string;
      destinationId?: string;
      capability?: string;
      renditionId?: string;
      renditionVersionId?: string;
    }) => {
      const query = new URLSearchParams();
      if (params?.workId) query.set("workId", params.workId);
      if (params?.contentProjectId)
        query.set("contentProjectId", params.contentProjectId);
      if (params?.destinationId) query.set("destinationId", params.destinationId);
      if (params?.capability) query.set("capability", params.capability);
      if (params?.renditionId) query.set("renditionId", params.renditionId);
      if (params?.renditionVersionId)
        query.set("renditionVersionId", params.renditionVersionId);
      const qs = query.toString();
      return `/publishing/publications/new${qs ? `?${qs}` : ""}`;
    },
  },

  channels: {
    destinations: "/channels/destinations",
    destinationDetail: (destinationId: string) =>
      `/channels/destinations/${destinationId}`,
    deliveries: "/channels/deliveries",
    deliveryDetail: (channelDeliveryId: string) =>
      `/channels/deliveries/${channelDeliveryId}`,
    published: "/channels/published",
    health: "/channels/health",
    healthCallback: "/channels/health/callback",
  },

  marketing: {
    root: "/marketing",
    campaigns: "/marketing/campaigns",
    campaignDetail: (campaignId: string) => `/marketing/campaigns/${campaignId}`,
  },

  sales: {
    lists: "/sales/lists",
    listDetail: (listId: string) => `/sales/lists/${listId}`,
    sequences: "/sales/sequences",
    sequenceDetail: (sequenceId: string) => `/sales/sequences/${sequenceId}`,
  },

  social: {
    /** OAuth callback — keep stable path registered in provider developer portals. */
    connectionsCallback: "/social/connections/callback",
    connections: "/channels/health",
    connectionDetail: (connectionId: string) =>
      `/channels/health?connection=${encodeURIComponent(connectionId)}`,
    channels: "/channels/destinations",
    channelDetail: (accountId: string) =>
      `/channels/destinations/${accountId}`,
    channelCompose: (accountId: string) =>
      `/publishing/quick?destinationId=${encodeURIComponent(accountId)}`,
    deliveries: "/channels/deliveries",
    channelStats: "/insights/overview",
    channelSetup: "/channels/health",
    channelSetupDetail: (socialChannelId: string) =>
      `/channels/health?provider=${encodeURIComponent(socialChannelId)}`,
    inbox: "/inbox?tab=network",
  },

  /**
   * Federated Inbox hub — Conversations | Network | Alerts.
   */
  inbox: {
    hub: "/inbox",
    conversations: "/inbox?tab=conversations",
    network: "/inbox?tab=network",
    alerts: "/inbox?tab=alerts",
    conversationThread: (threadId: string) =>
      `/inbox?tab=conversations&threadId=${encodeURIComponent(threadId)}`,
    networkItem: (itemId: string) =>
      `/inbox?tab=network&item=${encodeURIComponent(itemId)}`,
    settingsRouting: "/inbox?tab=conversations&settings=routing",
    settingsTags: "/inbox?tab=conversations&settings=tags",
    settingsSla: "/inbox?tab=conversations&settings=sla",
  },

  connectivity: {
    providers: "/connectivity/providers",
    providerConnect: "/social/connections/callback",
  },

  conversation: {
    /** Deep link — Conversation threads also live under Inbox → Conversations. */
    threads: "/conversation/threads",
  },

  planning: {
    root: "/planning",
    schedules: "/planning/schedules",
    calendar: "/planning/calendar",
    conflicts: "/planning/conflicts",
    conflictDetail: (conflictId: string) => `/planning/conflicts/${conflictId}`,
    executions: "/planning/executions",
    jobs: "/planning/jobs",
  },

  ai: {
    studio: "/ai/studio",
    assistant: "/ai/assistant",
    openrouter: "/settings/ai/providers",
    modelsNew: "/ai/models/new",
    modelConfigure: (modelTypeId: string) => `/ai/models/${modelTypeId}/configure`,
    gatewayConfigure: (_gatewayId: string) => "/settings/ai/providers",
  },

  metrics: {
    experiments: "/metrics/experiments",
    campaignAnalytics: "/metrics/campaigns",
    dashboards: "/metrics/dashboards",
    queries: "/metrics/queries",
  },

  insights: {
    root: "/insights",
    overview: "/insights/overview",
    plan: "/insights/plan",
    results: "/insights/results",
    audience: "/insights/audience",
    leads: "/insights/leads",
    messaging: "/insights/messaging",
    benchmarking: "/insights/benchmarking",
    content: "/insights/content",
    contentLibrary: "/insights/content/library",
    contentAds: "/insights/content/ads",
    contentEarnings: "/insights/content/earnings",
  },

  activity: {
    events: "/activity/events",
    notifications: "/activity/notifications",
    auditLogs: "/activity/audit-logs",
  },

  pulse: {
    root: "/pulse",
    signals: "/pulse/signals",
    signalDetail: (signalId: string) => `/pulse/signals/${signalId}`,
    trends: "/pulse/trends",
    trendDetail: (trendId: string) => `/pulse/trends/${trendId}`,
    sources: "/pulse/sources",
    sourceDetail: (sourceId: string) => `/pulse/sources/${sourceId}`,
    sourceNew: "/pulse/sources/new",
    alerts: "/pulse/alerts",
    alertDetail: (alertId: string) => `/pulse/alerts/${alertId}`,
    alertNew: "/pulse/alerts/new",
  },

  knowledge: {
    root: "/knowledge",
    taxonomies: "/knowledge/taxonomies",
    taxonomyDetail: (taxonomyId: string) => `/knowledge/taxonomies/${taxonomyId}`,
    tags: "/knowledge/tags",
    discover: "/knowledge/discover",
    bases: "/knowledge/bases",
  },

  engagement: {
    root: "/engagement",
    interactions: "/engagement/interactions",
    interactionDetail: (interactionId: string) =>
      `/engagement/interactions/${interactionId}`,
    interactionNew: "/engagement/interactions/new",
  },

  notification: {
    root: "/notification",
    templates: "/notification/templates",
    messages: "/notification/messages",
    deliveries: "/notification/deliveries",
  },

  entitlement: {
    subscriptions: "/entitlement/subscriptions",
  },

  compliance: {
    root: "/compliance",
    policies: "/compliance?tab=policies",
    violations: "/compliance?tab=violations",
    evaluations: "/compliance?tab=evaluations",
  },

  observability: {
    root: "/observability",
    outboundPublishing: "/observability?tab=outbound-publishing",
  },

  operation: {
    tickets: "/operation/tickets",
    incidents: "/operation/incidents",
    tasks: "/operation/tasks",
    escalations: "/operation/escalations",
    orgAdmin: "/operation/org-admin",
    orgAdminTeamMembers: "/operation/org-admin/team-members",
    orgAdminSettings: "/operation/org-admin/settings",
    orgAdminAiContext: "/operation/org-admin/ai-context",
    orgAdminAnalytics: "/operation/org-admin/analytics",
    orgAdminSecurity: "/operation/org-admin/security",
  },

  identity: {
    root: "/identity",
    onboarding: "/identity/onboarding",
  },

  settings: {
    root: "/settings",
    personal: "/settings/personal",
    notifications: "/settings/notifications",
    security: "/settings/security",
    /** Org-scoped which providers appear on Connections. */
    socialChannels: "/settings/social-channels",
    connectivityChannels: "/channels/health",
    connectivityChannelDetail: (socialChannelId: string) =>
      `/channels/health?provider=${encodeURIComponent(socialChannelId)}`,
    aiProviders: "/settings/ai/providers",
    platformAdmin: "/settings/platform-admin",
  },

  platformAdmin: {
    root: "/platform-admin",
    health: "/platform-admin/health",
    orgs: "/platform-admin/orgs",
    orgDetail: (orgId: string) => `/platform-admin/orgs/${orgId}`,
    users: "/platform-admin/users",
    userDetail: (userId: string) => `/platform-admin/users/${userId}`,
    plans: "/platform-admin/plans",
    auditLogs: "/platform-admin/audit-logs",
    baseTable: "/platform-admin/base-table",
    templates: "/platform-admin/templates",
    settings: "/platform-admin/settings",
  },
} as const;

export const catalogRoutes = domainRoutes.catalog;
